import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import ResourceCard from "@/components/resources/ResourceCard";
import { useLanguage } from "@/hooks/useLanguage";
import { localizeContentRow } from "@/lib/localizeContent";

const BLOG_SLUGS = [
  "fda-nmpa-approval-not-eu-mdr-readiness",
  "eu-mdr-operational-requirements-market-entry",
];
const WHITEPAPER_SLUGS = ["home-market-approval-eu-readiness"];

const EuMedtechRelatedContent = () => {
  const lang = useLanguage();

  const { data: items = [] } = useQuery({
    queryKey: ["eu-medtech-webinar-related", lang],
    queryFn: async () => {
      const blogCols = "id, slug, title, summary, featured_image, category, industry_category, service_category, published_at, author, tags";
      const wpCols = blogCols + ", title_es, summary_es";

      const [{ data: wp }, { data: bp }] = await Promise.all([
        supabase.from("white_papers").select(wpCols).in("slug", WHITEPAPER_SLUGS),
        supabase.from("blog_posts").select(blogCols).in("slug", BLOG_SLUGS).eq("lang", lang === "es" ? "es" : "en"),
      ]);

      const wpItems = (wp ?? []).map((r: any) => ({ ...localizeContentRow(r, lang), contentType: "white_papers" as const }));
      const bpItems = (bp ?? []).map((r: any) => ({ ...localizeContentRow(r, lang), contentType: "blog_posts" as const }));

      const ordered: any[] = [];
      WHITEPAPER_SLUGS.forEach((s) => {
        const m = wpItems.find((x: any) => x.slug === s);
        if (m) ordered.push(m);
      });
      BLOG_SLUGS.forEach((s) => {
        const m = bpItems.find((x: any) => x.slug === s);
        if (m) ordered.push(m);
      });
      return ordered;
    },
    staleTime: 5 * 60 * 1000,
  });

  if (!items || items.length === 0) return null;

  const subtitle = lang === "es" ? "Lecturas imprescindibles" : "Must-reads";
  const title = lang === "es" ? "Más recursos" : "More resources";

  return (
    <section className="py-20 sm:py-28 bg-secondary">
      <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-wider text-accent mb-2">{subtitle}</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground">{title}</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((post: any, i: number) => (
            <ResourceCard key={post.id} resource={post} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default EuMedtechRelatedContent;
